import {
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';

import { MarketingShell } from '../../components/marketing/MarketingShell';
import { type Exploration,explorationBySlug } from '../../data/explorations';
import { fetchJson } from '../../lib/api';

type VenueCard = {
  id: string;
  slug: string;
  name: string;
  city: string;
  country: string;
  capacityMin: number;
  capacityMax: number;
  verified: boolean;
  foundingMember: boolean;
  heroImageUrl: string | null;
  reviewSummary: { visible: boolean; reviewCount: number } | null;
};

type BrowseResponse = {
  ok: true;
  items: VenueCard[];
  nextCursor: string | null;
};

async function fetchBrowse(query: string): Promise<BrowseResponse> {
  return fetchJson<BrowseResponse>(`/api/browse?${query}`);
}

export const Route = createFileRoute('/explore/$slug')({
  beforeLoad: ({ params }) => {
    if (!explorationBySlug(params.slug)) {
      throw notFound();
    }
  },
  component: ExplorePage,
});

function ExplorePage() {
  const { slug } = Route.useParams();
  const exploration = explorationBySlug(slug);
  if (!exploration) return null; // beforeLoad already guarded; keeps the type narrow

  return (
    <MarketingShell>
      <Container size="lg" py="xl">
        <ExploreBody exploration={exploration} />
      </Container>
    </MarketingShell>
  );
}

function ExploreBody({ exploration }: { exploration: Exploration }) {
  const query = useQuery({
    queryKey: ['explore', exploration.slug],
    queryFn: () => fetchBrowse(exploration.browseQuery),
  });

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Text size="sm" tt="uppercase" c="brand.7" fw={600} style={{ letterSpacing: '0.1em' }}>
          Explore on WWAAVE
        </Text>
        <Title
          order={1}
          style={{
            fontFamily: 'var(--wwaave-font-display)',
            fontSize: 'clamp(2rem, 4.5vw, 3rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            fontWeight: 600,
          }}
        >
          {exploration.title}
        </Title>
        <Text c="ink.7" size="lg" style={{ maxWidth: 720 }}>
          {exploration.description}
        </Text>
      </Stack>

      {query.isLoading && <Loader />}

      {query.data && query.data.items.length === 0 && (
        <Card withBorder padding="xl" radius="md">
          <Stack gap="xs">
            <Text fw={500}>No venues match this view yet.</Text>
            <Text size="sm" c="dimmed">
              The platform is small and growing. New listings land on{' '}
              <Anchor component={Link} to="/browse">
                Browse
              </Anchor>{' '}
              first; this view fills in as more venues join.
            </Text>
          </Stack>
        </Card>
      )}

      {query.data && query.data.items.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {query.data.items.map((v) => (
            <VenueTile key={v.id} venue={v} />
          ))}
        </SimpleGrid>
      )}

      <Card withBorder padding="md" radius="md" bg="brand.0">
        <Group justify="space-between" wrap="wrap">
          <Stack gap={2}>
            <Text fw={500}>Looking for something more specific?</Text>
            <Text size="sm" c="dimmed">
              Browse with full filters: city radius, capacity, art form, vibe.
            </Text>
          </Stack>
          <Button component={Link} to="/browse" variant="light">
            Open the full browse
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}

function VenueTile({ venue }: { venue: VenueCard }) {
  return (
    <Link
      to="/venues/$slug"
      params={{ slug: venue.slug }}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <Card withBorder padding="md" radius="md">
        <Stack gap="xs">
          {venue.heroImageUrl && (
            <Card.Section>
              <img
                src={venue.heroImageUrl}
                alt={venue.name}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
              />
            </Card.Section>
          )}
          <Group justify="space-between" wrap="nowrap">
            <Text fw={500} truncate>
              {venue.name}
            </Text>
            {venue.foundingMember && (
              <Badge size="xs" color="sun" variant="light">
                Founding
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {venue.city}
          </Text>
          <Group gap="xs">
            {venue.verified && (
              <Badge size="xs" color="tide" variant="light">
                Verified
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              Capacity {venue.capacityMin}–{venue.capacityMax}
            </Text>
          </Group>
        </Stack>
      </Card>
    </Link>
  );
}
